import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '@/shared/i18n';
import { ROUTES } from '@/app/router/route-paths';
import { WorkerizeWordmark } from '@/shared/ui/brand';
import { PublicThemeToggle } from '@/app/layouts/public/PublicThemeToggle';
import { PublicLocaleToggle } from '@/app/layouts/public/PublicLocaleToggle';
import { cn } from '@/shared/lib/cn';

/**
 * SiteHeader — the single public top app bar (landing + all PublicLayout pages).
 *
 * - Transparent at the very top; on scroll (`scrollY > 40`) it becomes a frosted
 *   glass bar — a translucent surface (`--header-scrolled-bg`, see-through) plus
 *   `backdrop-blur` — so content shows through on every viewport size.
 * - Nav items route to the landing anchors via `/#section` (react-router Link),
 *   so they work from ANY public page (e.g. /register), not just the landing.
 *   `LandingPage` scrolls to the hash on arrival.
 * - Below `lg` the inline nav collapses into a hamburger that opens a full-width
 *   menu panel (nav + Sign in + theme/locale). The panel forces an opaque-ish
 *   surface so it stays readable even at the top of the page.
 * - Theme-aware via tokens; RTL-safe via logical utilities.
 */
export function SiteHeader(): ReactNode {
  const { t } = useT();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = (): void => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the mobile menu on Escape.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const nav: { to: string; label: string }[] = [
    { to: `${ROUTES.landing}#features`, label: t('landing.nav.pathway') },
    { to: `${ROUTES.landing}#intelligence`, label: t('landing.nav.intelligence') },
    { to: `${ROUTES.landing}#pricing`, label: t('landing.nav.pricing') },
  ];

  const showSurface = scrolled || menuOpen;

  return (
    <header
      data-testid="site-header"
      className={cn(
        'fixed top-0 z-50 w-full border-b transition-all duration-500 ease-standard',
        showSurface
          ? 'border-outline/10 bg-[var(--header-scrolled-bg)] shadow-[0_4px_20px_-5px_rgba(0,0,0,0.25)] backdrop-blur-xl'
          : 'border-transparent bg-transparent',
      )}
    >
      <div className="mx-auto flex h-20 max-w-container-max items-center justify-between gap-4 px-margin-mobile md:px-margin-desktop">
        <div className="flex items-center gap-6 lg:gap-10">
          <Link
            to={ROUTES.landing}
            aria-label={t('app.name')}
            data-testid="site-header-brand"
            onClick={() => {
              setMenuOpen(false);
              window.scrollTo({ top: 0 });
            }}
            className="inline-flex items-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary-fixed/60"
          >
            <WorkerizeWordmark size={36} />
          </Link>
          <nav
            className="hidden items-center gap-7 lg:flex"
            aria-label={t('app.name')}
          >
            {nav.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="text-xs font-semibold uppercase tracking-wider text-on-surface-variant transition-colors hover:text-secondary"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <PublicThemeToggle />
          <PublicLocaleToggle />
          <Link
            to={ROUTES.auth.login}
            data-testid="site-header-cta"
            className="hidden whitespace-nowrap rounded-lg border border-outline/30 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider text-on-surface transition-colors hover:bg-surface-container-low sm:inline-flex"
          >
            {t('landing.nav.login')}
          </Link>

          {/* Hamburger — below lg only. */}
          <button
            type="button"
            data-testid="site-header-menu-toggle"
            aria-label={t('landing.nav.menu')}
            aria-expanded={menuOpen}
            aria-controls="site-header-mobile-menu"
            onClick={() => setMenuOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-outline/30 text-on-surface transition-colors hover:bg-surface-container-low lg:hidden"
          >
            {menuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      {/* Mobile menu panel. */}
      {menuOpen && (
        <div
          id="site-header-mobile-menu"
          className="border-t border-outline/10 bg-[var(--header-scrolled-bg)] backdrop-blur-xl lg:hidden"
        >
          <nav
            className="mx-auto flex max-w-container-max flex-col gap-1 px-margin-mobile py-4"
            aria-label={t('app.name')}
          >
            {nav.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMenuOpen(false)}
                className="rounded-lg px-3 py-3 text-sm font-semibold uppercase tracking-wider text-on-surface-variant transition-colors hover:bg-surface-container-low hover:text-secondary"
              >
                {item.label}
              </Link>
            ))}
            <Link
              to={ROUTES.auth.login}
              onClick={() => setMenuOpen(false)}
              className="mt-2 rounded-lg border border-outline/30 px-3 py-3 text-center text-sm font-semibold uppercase tracking-wider text-on-surface transition-colors hover:bg-surface-container-low"
            >
              {t('landing.nav.login')}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}

function MenuIcon(): ReactNode {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 7h16M4 12h16M4 17h16"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CloseIcon(): ReactNode {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default SiteHeader;
